import type { ConfirmationDialogSection } from '../components/shared/confirmation-dialog/confirmation-dialog.component';
import {
  TimeOffOverlapCheckResultDto,
  TimeOffOverlapDto,
  TimeOffOverlapMatterDto,
} from '../models/time-off.models';
import { formatShortDateRange } from './time-off-days.utils';

/**
 * Wording shared by the two places the overlap check is shown: the Manage Time Off Request
 * dialog's callout and the "Approve anyway" confirmation on the add row. Kept together so a
 * resource reads the same way in both, however the result reached the screen.
 */

/** One line of the dialog's callout, shaped once so the template binds plain strings. */
export interface OverlapRow {
  timeOffRequestId: number;
  employeeName: string;
  statusName: string;
  /** The other request is still awaiting its own approval; the callout badges it differently. */
  isPending: boolean;
  /** From formatOverlapDays. */
  daysLabel: string;
  /** The shared matters, comma-separated, or '' when the API named none. */
  mattersLabel: string;
}

/**
 * The callout's lines for an answer, in the API's order. An answer with no overlap yields none,
 * whatever its list holds: `hasOverlap` is the API's verdict, and the list only explains it.
 */
export function toOverlapRows(
  result: TimeOffOverlapCheckResultDto,
): OverlapRow[] {
  if (!result.hasOverlap) return [];
  return result.overlaps.map((overlap) => ({
    timeOffRequestId: overlap.timeOffRequestId,
    employeeName: overlap.employeeName,
    statusName: overlap.statusName,
    isPending: (overlap.statusCode || '').toUpperCase() === 'PENDING',
    daysLabel: formatOverlapDays(overlap.days),
    mattersLabel: overlap.matters.map(formatOverlapMatter).join(', '),
  }));
}

/**
 * "Mar 2, 2026 - Mar 4, 2026 (3 working days)" for the shared days of one overlap, or the single
 * date with "(1 working day)". The API already sends the days ascending and without weekends or
 * holidays, so the count is the list's length.
 */
export function formatOverlapDays(days: string[]): string {
  if (!days.length) return '';
  const count = days.length;
  const range = formatShortDateRange(days[0], days[count - 1]);
  return `${range} (${count} working ${count === 1 ? 'day' : 'days'})`;
}

/**
 * "1001-0002 - Merger review" when the matter service answered with a name, otherwise the number
 * on its own. The client name is left out: the matter name is what the approver scans for, and
 * the number is always there to look the client up.
 */
export function formatOverlapMatter(matter: TimeOffOverlapMatterDto): string {
  const name = matter.matterName?.trim();
  return name
    ? `${matter.clientMatterNumber} - ${name}`
    : matter.clientMatterNumber;
}

/**
 * One line for the confirmation dialog's list, e.g. "Grace Hopper (Approved): Mar 2, 2026 -
 * Mar 3, 2026 (2 working days) on 1001-0002 - Merger review". The callout renders the same parts
 * structurally; this is for places that take plain strings.
 */
export function describeOverlap(overlap: TimeOffOverlapDto): string {
  const matters = overlap.matters.map(formatOverlapMatter).join(', ');
  const on = matters ? ` on ${matters}` : '';
  return `${overlap.employeeName} (${overlap.statusName}): ${formatOverlapDays(overlap.days)}${on}`;
}

/**
 * The heading over a confirmation's list of overlaps. The check returns pending requests as
 * well as approved ones, so "Already Approved" is used only when every row is approved; one
 * pending row makes it "Approved or Pending", so the heading never contradicts a row under it.
 */
export function overlapSectionHeading(overlaps: TimeOffOverlapDto[]): string {
  const allApproved = overlaps.every(
    (overlap) => (overlap.statusCode || '').toUpperCase() === 'APPROVED',
  );
  return allApproved ? 'Already Approved' : 'Approved or Pending';
}

/**
 * The overlaps as one headed section of a confirmation dialog: the status-aware heading over one
 * line per overlapping request. Both confirmations that warn before an approval build it here,
 * so the add row and the Manage dialog's edit preview list the same overlap the same way.
 */
export function overlapConfirmationSection(
  overlaps: TimeOffOverlapDto[],
): ConfirmationDialogSection {
  return {
    heading: overlapSectionHeading(overlaps),
    items: overlaps.map(describeOverlap),
  };
}
